/* AG discount choice (UK): the drawer's code box and the "pick one" popup.

   Ported from the EU store (Stealth-Equipment-EU-v2 #256, 7 Oct 2026).
   Discounts never combine (Michael, 7 Oct 2026). The drawer had no code
   box (codes went in at checkout, which silently keeps one discount);
   this adds one back, and when the code clashes with what the cart
   already has (another code, or the builder bundle saving) we work out
   what each option is worth on its own and let the customer pick one.

   How each option is priced:
   - The cart's code list is set exactly with /cart/update.js
     { discount: "CODE" } (or "" to clear). Never /discount/<junk> to
     clear: Shopify keeps a LIST of codes, so a junk code is only added.
   - The bundle saving is switched off by setting every builder line's
     _builder property 'true' -> 'off'; the bundle discount function
     only prices lines whose _builder is 'true'. Switching back
     restores it.
   - Each option is applied in turn and /cart.js read back, so every
     amount shown is Shopify's own number for this cart.

   Safety net: a cart left with bundle lines 'off' and no working code
   is switched back on (heal) on load and after every drawer refresh,
   and a flip interrupted by leaving the page is finished by heal via a
   sessionStorage marker.

   Self-contained: the drawer's own script (cart-drawer-extras.js) is
   minified and still carries the old, retired code-box handler bound to
   [data-agcd-disc]; this box uses [data-agdc-*] so the two never meet.
   Every request checks r.ok; every loop is bounded; cart writes go
   through window.AGCart.run when present. */
(function () {
  'use strict';
  if (window.AGDiscountChoice) return;

  var PACK_ON = 'true';
  var PACK_OFF = 'off';
  var MAX_TOGGLE_STEPS = 40;
  var busy = false;
  /* While prices are being compared the cart is mid-change: block the
     checkout button so nobody leaves with the bundle saving half off. */
  function setBusy(on) {
    busy = on;
    try { document.documentElement.classList.toggle('agdc-busy', on); } catch (e) {}
  }

  var currency = 'GBP';
  function money(cents) {
    var v = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency }).format(v).replace(/\.00$/, '');
    } catch (e) {
      return '£' + v.toFixed(2).replace(/\.00$/, '');
    }
  }

  /* Re-render the WHOLE drawer from the server (the same section swap
     cart-drawer-extras does internally but does not expose):
     cart-form.refresh() only merges the [data-merge] regions, and the
     discount note row is outside them. Then 'cart:refresh' for the
     other listeners. */
  function hardRefresh(msg) {
    if (msg && msg.text) toast(msg.text, msg.kind);
    var drawer = document.querySelector('cart-drawer');
    var sectionId = drawer && drawer.getAttribute('data-section-id');
    if (!sectionId) { document.dispatchEvent(new CustomEvent('cart:refresh')); return; }
    fetch('/?section_id=' + encodeURIComponent(sectionId) + '&_agdc=' + Date.now(), { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('section ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('cart-form');
        var old = drawer.querySelector('cart-form');
        if (fresh && old) {
          old.innerHTML = fresh.innerHTML;
          if (window.theme && typeof theme.manuallyLoadImages === 'function') {
            try { theme.manuallyLoadImages(drawer); } catch (e) {}
          }
        }
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(function () { document.dispatchEvent(new CustomEvent('cart:refresh')); });
  }

  var toastTimer = null;
  function toast(text, kind) {
    var t = document.querySelector('[data-agdc-toast]');
    if (!t) return;
    t.textContent = text;
    t.className = 'agdc-toast is-' + (kind || 'success') + ' is-open';
    t.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      t.classList.remove('is-open');
      setTimeout(function () { t.hidden = true; }, 250);
    }, 4500);
  }

  function cartWrite(fn) {
    return (window.AGCart && typeof window.AGCart.run === 'function') ? window.AGCart.run(fn) : fn();
  }

  function getCart() {
    /* Absolute URL on purpose: ag-cart-read-cache replays recent
       relative /cart.js reads, and these decisions need a fresh cart. */
    return fetch(location.origin + '/cart.js?_agdc=' + Date.now(), { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      })
      .then(function (c) { if (c && c.currency) currency = c.currency; return c; });
  }

  var MAX_THROTTLE_RETRIES = 3;
  function wait(ms) { return new Promise(function (res) { setTimeout(res, ms); }); }

  /* One POST, retried only on 429 (Shopify throttling), at most
     MAX_THROTTLE_RETRIES times with growing waits. Not queued: callers
     run it inside cartWrite. */
  function rawPost(url, body, attempt) {
    attempt = attempt || 0;
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (r.status === 429 && attempt < MAX_THROTTLE_RETRIES) {
        return wait(600 * (attempt + 1)).then(function () { return rawPost(url, body, attempt + 1); });
      }
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  function postJson(url, body) {
    return cartWrite(function () { return rawPost(url, body); });
  }

  /* Replace the cart's whole code list. [] clears it. */
  function setCodes(codes) {
    return postJson('/cart/update.js', { discount: (codes || []).join(',') });
  }

  function upper(s) { return String(s || '').toUpperCase(); }

  function workingCodes(c) {
    return ((c && c.discount_codes) || [])
      .filter(function (d) { return d.applicable; })
      .map(function (d) { return d.code; });
  }

  function isPackLine(it, state) {
    var p = (it && it.properties) || {};
    return !!p._bundle_id && p._builder === state;
  }

  function hasPackLines(c, state) {
    return ((c && c.items) || []).some(function (it) { return isPackLine(it, state); });
  }

  /* Flip every pack line one at a time. Each flip reads the cart and
     writes inside ONE queued operation, and addresses the line by its
     KEY, never by position: another script (agpb-cart-validate) can
     remove lines between a read and a write, and a stale position
     would rewrite a neighbouring line. A key that has gone fails with
     a 4xx instead. Quantity is the sum of every entry sharing the key
     (a BXGY code can split one line into two entries). Bounded. */
  function setPack(on) {
    var from = on ? PACK_OFF : PACK_ON;
    var to = on ? PACK_ON : PACK_OFF;
    var steps = 0;
    function flipOne() {
      return getCart().then(function (c) {
        var items = c.items || [];
        var it = null;
        for (var i = 0; i < items.length; i++) {
          if (isPackLine(items[i], from)) { it = items[i]; break; }
        }
        if (!it) return null;
        var qty = 0;
        items.forEach(function (x) { if (x.key === it.key) qty += x.quantity; });
        var props = {};
        Object.keys(it.properties || {}).forEach(function (k) { props[k] = it.properties[k]; });
        props._builder = to;
        return rawPost('/cart/change.js', { id: it.key, quantity: qty, properties: props });
      });
    }
    function step() {
      return cartWrite(flipOne).then(function (res) {
        if (!res) return null;
        steps += 1;
        if (steps >= MAX_TOGGLE_STEPS) throw new Error('pack toggle did not settle');
        return step();
      });
    }
    return step();
  }

  /* Put the cart into a state, then read it back. Codes are cleared
     BEFORE the pack lines change, so no code can be splitting a line
     while its properties are rewritten. The pending marker lets heal()
     finish the job if the page goes away mid-way. */
  var PENDING_KEY = 'agdcPending';
  function markPending(on) {
    try { if (on) sessionStorage.setItem(PENDING_KEY, '1'); else sessionStorage.removeItem(PENDING_KEY); } catch (e) {}
  }
  function isPending() {
    try { return sessionStorage.getItem(PENDING_KEY) === '1'; } catch (e) { return false; }
  }
  function applyState(st) {
    return setCodes([])
      .then(function () { return setPack(st.pack); })
      .then(function () { return st.codes && st.codes.length ? setCodes(st.codes) : null; })
      .then(getCart);
  }

  /* ---------- popup ---------- */

  function modalEl() { return document.querySelector('[data-agcd-dchoice]'); }

  /* options: [{ key, label, amount }]. Resolves with the chosen key, or
     null when dismissed (overlay, Escape, "keep" link). Shown biggest
     saving first; that one is marked and focused. */
  function ask(options) {
    return new Promise(function (resolve) {
      var m = modalEl();
      if (!m) { resolve(null); return; }
      var opener = document.activeElement;
      var list = m.querySelector('[data-agcd-dchoice-opts]');
      list.innerHTML = '';
      options = options.slice().sort(function (a, b) { return b.amount - a.amount; });
      /* Only call one "Mayor ahorro" when it really is bigger (no tie). */
      var best = (options.length > 1 && options[0].amount > options[1].amount) ? options[0] : null;
      var settled = false;
      function finish(key) {
        if (settled) return;
        settled = true;
        document.removeEventListener('keydown', onKey);
        m.removeEventListener('click', onClick);
        m.classList.remove('is-open');
        setTimeout(function () { m.hidden = true; }, 200);
        try { var inp = document.querySelector('[data-agdc-input]'); (inp || opener).focus(); } catch (e) {}
        resolve(key);
      }
      function onKey(e) { if (e.key === 'Escape' || e.key === 'Esc') finish(null); }
      function onClick(e) {
        if (e.target.closest('[data-agcd-dchoice-close]')) { e.preventDefault(); finish(null); return; }
        var opt = e.target.closest('[data-agcd-dchoice-key]');
        if (opt) { e.preventDefault(); finish(opt.getAttribute('data-agcd-dchoice-key')); }
      }
      options.forEach(function (o) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'agcd-dchoice__opt' + (o === best ? ' is-best' : '');
        b.setAttribute('data-agcd-dchoice-key', o.key);
        var name = document.createElement('span');
        name.className = 'agcd-dchoice__name';
        name.textContent = o.label;
        var amt = document.createElement('span');
        amt.className = 'agcd-dchoice__amt';
        amt.textContent = o.amount > 0 ? 'Save ' + money(o.amount) : 'Shipping discount';
        b.appendChild(name);
        b.appendChild(amt);
        if (o === best) {
          var tag = document.createElement('span');
          tag.className = 'agcd-dchoice__tag';
          tag.textContent = 'Best saving';
          b.appendChild(tag);
        }
        list.appendChild(b);
      });
      m.hidden = false;
      void m.offsetWidth; /* commit hidden=false first so the fade runs; no rAF, which stalls in background tabs */
      m.classList.add('is-open');
      m.addEventListener('click', onClick);
      document.addEventListener('keydown', onKey);
      var first = list.querySelector('.is-best') || list.querySelector('button');
      if (first) setTimeout(function () { first.focus(); }, 60);
    });
  }

  function refresh(text, kind) {
    hardRefresh(text ? { kind: kind || 'success', text: text } : undefined);
  }

  /* ---------- the choice ---------- */

  function codesLabel(codes) {
    return (codes.length > 1 ? 'Codes ' : 'Code ') + codes.join(' + ');
  }

  /* o.code: what the customer just entered. o.before: /cart.js taken
     before it was applied. Resolves to:
       { ok: true }                    handled (popup shown, or no clash)
       { ok: false, invalid: true, cart } code is no good on its own
       { ok: false }                   not handled, caller falls back */
  function resolve(o) {
    if (busy || !o || !o.before) return Promise.resolve({ ok: false });
    setBusy(true);
    markPending(true);
    var before = o.before;
    var entered = upper(o.code);
    var prevCodes = workingCodes(before).filter(function (c) { return upper(c) !== entered; });
    var packOnBefore = hasPackLines(before, PACK_ON);
    var anyPack = packOnBefore || hasPackLines(before, PACK_OFF);
    /* Restore the pack exactly as it was: off only if it was off. */
    var beforeState = { pack: !(anyPack && !packOnBefore), codes: prevCodes };
    var options = [];
    var states = {};

    /* The "before" option: whatever saving the cart had. */
    var prevLabel = prevCodes.length
      ? (packOnBefore ? 'Bundle saving + ' + codesLabel(prevCodes) : codesLabel(prevCodes))
      : (packOnBefore ? 'Bundle saving' : 'Current discount');
    if ((before.total_discount || 0) > 0) {
      options.push({ key: 'prev', label: prevLabel, amount: before.total_discount });
      states.prev = beforeState;
    }

    var newState = { pack: false, codes: [o.code] };

    var chain = Promise.resolve();
    /* Pack switched off earlier for a code: offer the pack again too. */
    if (anyPack && !packOnBefore && prevCodes.length) {
      chain = chain.then(function () { return applyState({ pack: true, codes: [] }); })
        .then(function (c) {
          if ((c.total_discount || 0) > 0) {
            options.push({ key: 'pack', label: 'Bundle saving', amount: c.total_discount });
            states.pack = { pack: true, codes: [] };
          }
        });
    }

    return chain
      .then(function () { return applyState(newState); })
      .then(function (c) {
        var m = (c.discount_codes || []).find(function (d) { return upper(d.code) === entered; });
        var amount = c.total_discount || 0;
        /* A shipping-only code is applicable with 0 item discount
           (total_discount leaves shipping out), so trust applicable. */
        if (!m || !m.applicable) {
          return applyState(beforeState).then(function (back) { return { ok: false, invalid: true, cart: back }; });
        }
        var newLabel = 'Code ' + m.code + (amount > 0 ? '' : ' (shipping)');
        if (!options.length) {
          /* Nothing to clash with after all: keep the code. */
          refresh('Code applied.');
          return { ok: true };
        }
        options.push({ key: 'new', label: newLabel, amount: amount });
        states['new'] = newState;
        var labels = {};
        options.forEach(function (op) { labels[op.key] = op.label; });
        return ask(options).then(function (key) {
          var pick = key && states[key] ? key : 'prev';
          if (!states[pick]) pick = 'new';
          if (pick === 'new') { refresh('Applied: ' + newLabel + '.'); return { ok: true }; }
          return applyState(states[pick]).then(function () {
            refresh('Applied: ' + labels[pick] + '.');
            return { ok: true };
          });
        });
      })
      .catch(function () {
        return applyState(beforeState)
          .catch(function () {})
          .then(function () {
            refresh('Something went wrong. Please check your basket.', 'error');
            return { ok: true };
          });
      })
      .then(function (res) { setBusy(false); markPending(false); return res; },
            function () { setBusy(false); markPending(false); return { ok: true }; });
  }

  /* Pack lines are off but no code is working (code removed at
     checkout, expired, or the cart changed): switch the pack back on. */
  function heal() {
    if (busy) return Promise.resolve(false);
    return getCart().then(function (c) {
      if (busy) return false; /* a choice started while we were reading */
      var interrupted = isPending();
      if (!hasPackLines(c, PACK_OFF)) { if (interrupted) markPending(false); return false; }
      /* Pack off with a working code is a choice the customer made,
         unless a flip was interrupted (pending marker): then the cart
         may be half switched, so put the pack back and drop codes. */
      if (workingCodes(c).length && !interrupted) return false;
      setBusy(true);
      var work = interrupted ? setCodes([]).then(function () { return setPack(true); }) : setPack(true);
      return work.then(function () {
        setBusy(false);
        markPending(false);
        hardRefresh();
        return true;
      }, function () { setBusy(false); return false; });
    }).catch(function () { return false; });
  }

  /* Clear every code (and get the pack back via heal). */
  function clearAll() {
    if (busy) return Promise.resolve(false);
    return setCodes([]).then(heal);
  }

  /* ---------- the drawer's code box ---------- */

  var MSG_INVALID = "That code isn't valid for this basket. Please check it and try again.";
  var MSG_LIFETIME_NEEDS = 'LIFETIME needs a pair of Black, Chrome or Core Edition dumbbells in your basket.';
  /* Code LIFETIME adds its free warranty (assets/ag-lifetime-warranty.js).
     The LIFETIME hint only when the basket has no qualifying pair;
     otherwise (used before, ended) the plain message. */
  function isLifetime(entered) {
    return !!window.AGLifetime && entered === window.AGLifetime.CODE;
  }
  function invalidMsg(entered, c) {
    return (isLifetime(entered) && c && window.AGLifetime.pairs(c) < 1) ? MSG_LIFETIME_NEEDS : MSG_INVALID;
  }

  function applyCode(code, form) {
    var input = form.querySelector('[data-agdc-input]');
    var btn = form.querySelector('[data-agdc-apply]');
    var msgEl = form.parentNode.querySelector('[data-agdc-msg]');
    function show(kind, text) {
      if (!msgEl) return;
      msgEl.className = 'agcd-disc__msg is-' + kind;
      msgEl.textContent = text;
      msgEl.hidden = false;
    }
    function clear() { if (msgEl) { msgEl.hidden = true; msgEl.textContent = ''; } }
    function done() { form.classList.remove('is-loading'); if (btn) btn.disabled = false; }
    if (busy) return;
    clear();
    show('pending', 'Checking the code…');
    form.classList.add('is-loading');
    if (btn) btn.disabled = true;
    var entered = upper(code);
    getCart().then(function (before) {
      /* Bundle lines left off with no working code: put the saving back
         first so the comparison starts from the real cart. */
      if (hasPackLines(before, PACK_OFF) && !workingCodes(before).length) {
        return setPack(true).then(getCart);
      }
      return before;
    }).then(function (before) {
      /* LIFETIME: put the warranty in first, so the code has something
         to make free when it is checked (from 1 Nov it only applies
         then). The comparison still starts from the cart as it was, so
         another code's saving is not priced against the added line. If
         LIFETIME does not stick, the warranty pass takes the line back. */
      if (!isLifetime(entered)) return before;
      window.AGLifetime.setApplying(true);
      return window.AGLifetime.sync(true).then(function () { return before; });
    }).then(function (before) {
      if (workingCodes(before).some(function (c) { return upper(c) === entered; })) {
        show('warning', 'Code ' + code + ' is already applied.');
        done();
        return null;
      }
      if ((before.total_discount || 0) > 0 || workingCodes(before).length) {
        /* Something is already saving money: the customer picks one. */
        show('pending', 'Comparing discounts…');
        return resolve({ code: code, before: before }).then(function (res) {
          if (res.ok) { clear(); if (input) input.value = ''; done(); return null; }
          show('error', res.invalid ? invalidMsg(entered, res.cart) : 'Please try again in a moment.');
          done();
          return null;
        });
      }
      /* Nothing to clash with: just apply it. */
      return setCodes([code]).then(getCart).then(function (c) {
        var m = (c.discount_codes || []).find(function (d) { return upper(d.code) === entered; });
        if (m && m.applicable) {
          clear();
          if (input) input.value = '';
          done();
          hardRefresh({ kind: 'success', text: 'Code ' + m.code + ' applied.' });
          return null;
        }
        return setCodes([]).then(function () { show('error', invalidMsg(entered, c)); done(); return null; });
      });
    }).catch(function () {
      show('error', 'Something went wrong. Please try again.');
      done();
    }).then(function () {
      /* Settle the warranty line now: a LIFETIME that did not stick
         leaves no redraw behind. */
      if (isLifetime(entered)) { window.AGLifetime.setApplying(false); window.AGLifetime.schedule(); }
    });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest && e.target.closest('[data-agdc-form]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('[data-agdc-input]');
    var code = (input && input.value || '').trim();
    if (code) applyCode(code, form);
  });

  /* The "x" on the applied-code note: clear every code; heal() then
     brings back a bundle saving that was switched off for the code. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-agdc-remove]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    btn.disabled = true;
    clearAll().then(function () {
      hardRefresh({ kind: 'success', text: 'Code removed.' });
    }, function () {
      btn.disabled = false;
      toast('Something went wrong. Please try again.', 'error');
    });
  });

  window.AGDiscountChoice = {
    resolve: resolve,
    heal: heal,
    clearAll: clearAll,
    refresh: hardRefresh,
    isBusy: function () { return busy; }
  };

  /* heal() only acts when pack lines are off with no working code, and
     afterwards none are off, so it cannot loop. */
  var healTimer = null;
  function scheduleHeal() {
    clearTimeout(healTimer);
    healTimer = setTimeout(heal, 400);
  }
  document.addEventListener('cart:refresh', scheduleHeal);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleHeal);
  } else {
    scheduleHeal();
  }
})();
